(() => {
  const initializeShopSheet = (root) => {
    if (root.dataset.comelyShopReady === 'true') return;

    const dialog = root.querySelector('[data-comely-shop-dialog]');
    if (!(dialog instanceof HTMLDialogElement)) return;
    root.dataset.comelyShopReady = 'true';

    const openSelector = '[data-comely-shop-open]';
    const mobile = window.matchMedia('(max-width: 1024px)');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const panel = dialog.querySelector('.comely-shop-sheet__panel');
    const chatSpring = 'linear(0, .025 2.8%, .101 6.1%, .539 18.9%, .721 25.3%, .849 31.5%, .937 38.1%, .991 45.7%, 1.015 55%, 1.017 63.9%, 1.001)';
    const spring = window.CSS?.supports('animation-timing-function', chatSpring) ? chatSpring : 'cubic-bezier(.23, 1, .32, 1)';
    let panelAnimations = [];
    let closing = false;
    const cancelMotion = () => {
      panelAnimations.forEach(animation => animation.cancel());
      panelAnimations = [];
    };
    let returnFocus = null;
    let pageState = null;
    const footer = dialog.querySelector('.comely-shop-sheet__footer');
    const dock = dialog.querySelector('.comely-shop-sheet__dock');
    const body = dialog.querySelector('.comely-shop-sheet__body');
    const rootView = dialog.querySelector('[data-comely-shop-view="root"]');
    const views = [...dialog.querySelectorAll('[data-comely-shop-view]')];
    const submenuTriggers = [...dialog.querySelectorAll('[data-comely-shop-submenu]')];
    let activeSubmenuTrigger = null;
    // Keep the floating dock out of the panel's sliding transform.
    if (footer) dialog.append(footer);

    const preservePage = () => {
      const html = document.documentElement;
      const viewportWidth = html.clientWidth;
      pageState = { x: window.scrollX, y: window.scrollY, width: html.style.width };
      const toolbar = document.querySelector('[data-comely-toolbar]');
      if (toolbar && dock) {
        const rect = toolbar.getBoundingClientRect();
        const computed = getComputedStyle(toolbar);
        const width = parseFloat(computed.width);
        const transform = computed.transform;
        if (width > 0 && rect.width > 0) {
          pageState.toolbar = toolbar;
          pageState.toolbarStyles = {};
          ['width', 'transform', 'transition', 'left', 'bottom'].forEach((name) => {
            pageState.toolbarStyles[name] = toolbar.style[name];
          });
          toolbar.style.transition = 'none';
          toolbar.style.width = `${width}px`;
          toolbar.style.transform = transform;
          toolbar.style.left = `${rect.left + rect.width / 2}px`;
          toolbar.style.bottom = `${window.innerHeight - rect.bottom}px`;
          dock.style.setProperty('--shop-dock-width', `${width}px`);
          dock.style.setProperty('--shop-dock-scale', String(rect.width / width));
          dock.style.setProperty('--shop-dock-center', `${rect.left + rect.width / 2}px`);
          dock.style.setProperty('--shop-dock-bottom', `${window.innerHeight - rect.bottom}px`);
        }
      }
      // Removing the scrollbar must not reflow the background or move its anchor.
      html.style.width = `${viewportWidth}px`;
    };
    const restorePosition = () => {
      if (pageState) window.scrollTo({ left: pageState.x, top: pageState.y, behavior: 'instant' });
    };

    const syncOpeners = (open) => {
      document.querySelectorAll(openSelector).forEach((control) => {
        control.setAttribute('aria-expanded', String(open));
      });
    };

    const resetSubmenu = (restoreFocus = false) => {
      const trigger = activeSubmenuTrigger;
      views.forEach((view) => {
        const isRoot = view === rootView;
        view.hidden = !isRoot;
        view.classList.toggle('is-active', isRoot);
      });
      submenuTriggers.forEach((control) => control.setAttribute('aria-expanded', 'false'));
      activeSubmenuTrigger = null;
      if (body) body.scrollTop = 0;
      if (restoreFocus && trigger?.isConnected) trigger.focus({ preventScroll: true });
    };

    const openSubmenu = (trigger) => {
      const submenuName = trigger.dataset.comelyShopSubmenu;
      const view = views.find((item) => item.dataset.comelyShopView === submenuName);
      if (!view || !rootView) return;
      rootView.hidden = true;
      rootView.classList.remove('is-active');
      view.hidden = false;
      view.classList.add('is-active');
      submenuTriggers.forEach((control) => control.setAttribute('aria-expanded', String(control === trigger)));
      activeSubmenuTrigger = trigger;
      if (body) body.scrollTop = 0;
      view.querySelector('[data-comely-shop-back]')?.focus({ preventScroll: true });
    };

    const clearShopHash = () => {
      if (window.location.hash !== '#ComelyShopDialog') return;
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}`);
    };

    const restoreClosedState = (restoreFocus = true) => {
      cancelMotion();
      closing = false;
      dialog.classList.remove('is-closing');
      document.documentElement.classList.remove('comely-shop-open');
      resetSubmenu();
      if (pageState) {
        document.documentElement.style.width = pageState.width;
        restorePosition();
        if (pageState.toolbar?.isConnected) {
          Object.entries(pageState.toolbarStyles).forEach(([name, value]) => {
            pageState.toolbar.style[name] = value;
          });
        }
        pageState = null;
      }
      syncOpeners(false);
      clearShopHash();
      if (restoreFocus && returnFocus?.isConnected && returnFocus.getClientRects().length) {
        returnFocus.focus({ preventScroll: true });
      }
      returnFocus = null;
    };

    const closeDialog = (restoreFocus = true) => {
      if (!dialog.open) return;
      const finishClose = () => {
        dialog.close();
        restoreClosedState(restoreFocus);
      };
      // Handoffs must release the native modal before opening Chat/Offers/Cart.
      if (!restoreFocus || reducedMotion.matches || !panel?.animate) {
        finishClose();
        return;
      }
      if (closing) return;
      const current = getComputedStyle(panel);
      const from = { transform: current.transform, opacity: current.opacity };
      cancelMotion();
      closing = true;
      dialog.classList.add('is-closing');
      const exit = panel.animate([from, { transform: 'scale(.2)', opacity: 0 }], {
        duration: 150, easing: 'cubic-bezier(.25, 0, .1, 1)', fill: 'forwards'
      });
      panelAnimations = [exit];
      exit.finished.then(() => { if (closing) finishClose(); }).catch(() => {});
    };

    const openDialog = (source) => {
      if (!root.isConnected) return;
      clearShopHash();
      if (!mobile.matches || dialog.open || document.querySelector('dialog:modal')) return;
      returnFocus = source || document.querySelector(openSelector);
      resetSubmenu();
      preservePage();
      document.documentElement.classList.add('comely-shop-open');
      dialog.showModal();
      if (!reducedMotion.matches && panel?.animate) {
        panelAnimations = [
          panel.animate([{ transform: 'scale(.2)' }, { transform: 'scale(1)' }], { duration: 320, easing: spring }),
          panel.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 150, easing: 'cubic-bezier(.25, 0, .1, 1)' })
        ];
      }
      syncOpeners(true);
      dialog.querySelector('[data-comely-shop-close]').focus({ preventScroll: true });
      restorePosition();
      requestAnimationFrame(() => { if (dialog.open) restorePosition(); });
    };

    document.addEventListener('click', (event) => {
      const control = event.target instanceof Element ? event.target.closest(openSelector) : null;
      if (!control) return;
      event.preventDefault();
      openDialog(control);
    });

    dialog.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const submenuTrigger = event.target.closest('[data-comely-shop-submenu]');
      if (submenuTrigger) {
        event.preventDefault();
        openSubmenu(submenuTrigger);
        return;
      }
      if (event.target.closest('[data-comely-shop-back]')) {
        event.preventDefault();
        resetSubmenu(true);
        return;
      }
      if (event.target === dialog || event.target.closest('[data-comely-shop-close]')) {
        event.preventDefault();
        closeDialog();
        return;
      }

      const offers = event.target.closest('[data-comely-shop-offers]');
      const chat = event.target.closest('[data-comely-shop-chat]');
      const cart = event.target.closest('[aria-controls="CartDrawer"]');
      if (offers || chat || cart) {
        const selector = offers
          ? '[data-comely-discount-open]'
          : chat
            ? '[data-comely-chat-open]'
            : '[aria-controls="CartDrawer"]';
        const control = document.querySelector(`[data-comely-toolbar] ${selector}`);
        if (control && !control.disabled) {
          event.preventDefault();
          event.stopPropagation();
          closeDialog(false);
          control.focus({ preventScroll: true });
          control.click();
          return;
        }
      }

      if (event.target.closest('a[href]')) closeDialog(false);
    });

    dialog.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const controls = [...dialog.querySelectorAll('a[href], button:not(:disabled)')].filter((control) => control.getClientRects().length);
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    });

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeDialog();
    });
    dialog.addEventListener('close', () => {
      if (!dialog.open) restoreClosedState();
    });
    mobile.addEventListener('change', () => {
      if (!mobile.matches) closeDialog(false);
    });
    const openFromHash = () => {
      if (window.location.hash === '#ComelyShopDialog') openDialog();
    };
    window.addEventListener('hashchange', openFromHash);
    syncOpeners(false);
    openFromHash();
  };

  const initializeAll = (scope = document) => {
    scope.querySelectorAll('[data-comely-shop-sheet]').forEach(initializeShopSheet);
  };
  initializeAll();
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
